import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Users, Calendar, Image as ImageIcon, Crown, Newspaper, BookOpen, ExternalLink, Play, FileText, ArrowRight, X, ChevronRight, Globe, Lock, CreditCard, MessageSquare, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '@/utils/imageUrl';
import EventRegistrationForm from '@/components/EventRegistrationForm';

const API = `${API_URL}/api`;
// 101 Kanya Vivah is a separately deployed app. Override via REACT_APP_KANYA_VIVAH_URL if needed.
const KANYA_VIVAH_URL = process.env.REACT_APP_KANYA_VIVAH_URL || 'https://kanyavivah.lionsclubhawamahal.com';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('email');
  const [loading, setLoading] = useState(false);
  const [news, setNews] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showBlogsModal, setShowBlogsModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [externalLinkUrl, setExternalLinkUrl] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationEvent, setRegistrationEvent] = useState(null);
  const [registrationForm, setRegistrationForm] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  
  const [emailData, setEmailData] = useState({ email: '', password: '', name: '', mobile: '' });
  const [mobileData, setMobileData] = useState({ mobile: '', otp: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [signupOtpStep, setSignupOtpStep] = useState(false);  // For signup OTP verification
  const [signupToken, setSignupToken] = useState('');  // Token for signup OTP verification
  const [signupOtp, setSignupOtp] = useState('');  // OTP entered by user for signup
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotPasswordData, setForgotPasswordData] = useState({ email: '', reset_code: '', new_password: '', confirm_password: '' });

  useEffect(() => {
    loadPublicContent();
    loadBankDetails();
  }, []);

  const loadPublicContent = async () => {
    try {
      const [newsRes, blogsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/news`),
        axios.get(`${API}/blogs`),
        axios.get(`${API}/events/public`)
      ]);
      setNews(newsRes.data.slice(0, 6));
      setBlogs(blogsRes.data.slice(0, 6));
      setEvents(eventsRes.data.slice(0, 6));
    } catch (error) {
      console.log('Failed to load public content', error);
    }
  };

  const loadBankDetails = async () => {
    try {
      const response = await axios.get(`${API}/payments/bank-details/public`);
      setBankDetails(response.data);
    } catch (error) {
      console.log('No bank details available');
    }
  };

  const handleOpenRegistration = async (event) => {
    // Check if event is members only
    if (event.visibility === 'members_only') {
      toast.info('This event is for members only. Please login to register.');
      setShowAuth(true);
      return;
    }

    // Load the registration form
    if (event.registration_form_id) {
      try {
        const response = await axios.get(`${API}/forms/public/${event.registration_form_id}`);
        setRegistrationForm(response.data);
        setRegistrationEvent(event);
        setShowRegistrationModal(true);
        setSelectedEvent(null);
      } catch (error) {
        toast.error('Failed to load registration form');
      }
    } else {
      toast.info('No registration form available for this event');
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/app';
    const authUrl = 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isSignup) {
        // For signup, use the new OTP-based flow
        const response = await axios.post(`${API}/auth/signup/send-otp`, emailData);
        setSignupToken(response.data.signup_token);
        setSignupOtpStep(true);
        
        if (response.data.channel === 'whatsapp') {
          toast.success('OTP sent to your WhatsApp!');
        } else {
          toast.success('OTP sent! Check server logs.');
        }
      } else {
        // For login, use the regular flow
        const response = await axios.post(`${API}/auth/login`, emailData);
        localStorage.setItem('session_token', response.data.session_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        sessionStorage.setItem('just_authenticated', 'true');
        navigate('/app', { replace: true, state: { user: response.data.user } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySignupOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/signup/verify-otp`, {
        mobile: emailData.mobile,
        otp: signupOtp,
        signup_token: signupToken
      });
      
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      sessionStorage.setItem('just_authenticated', 'true');
      toast.success('Account created successfully!');
      navigate('/app', { replace: true, state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/otp/send`, { mobile: mobileData.mobile });
      toast.success('OTP sent! Check server logs.');
      setOtpSent(true);
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/otp/verify`, mobileData);
      // Store token and user in localStorage
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      sessionStorage.setItem('just_authenticated', 'true');
      navigate('/app', { replace: true, state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(forgotPasswordData.email)}`);
      toast.success('Reset code sent! Please check your email inbox.');
      setForgotPasswordStep(2);
    } catch (error) {
      toast.error('Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (forgotPasswordData.new_password !== forgotPasswordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (forgotPasswordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotPasswordData.email,
        code: forgotPasswordData.reset_code,
        new_password: forgotPasswordData.new_password
      });
      toast.success('Password reset successfully! Please login.');
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
      setForgotPasswordData({ email: '', reset_code: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (showAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}>
        <Card className="w-full max-w-md" data-testid="auth-card">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2" style={{ color: '#0F172A', fontWeight: '700' }}>Lions Club</h1>
              <p className="text-sm font-semibold" style={{ color: '#D4AF37' }}>Jaipur Hawamahal</p>
            </div>
            
            {showForgotPassword ? (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-center mb-4" style={{ color: '#0F172A' }}>Reset Password</h2>
                
                {forgotPasswordStep === 1 && (
                  <form onSubmit={handleForgotPasswordSendCode} className="space-y-4">
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={forgotPasswordData.email}
                      onChange={(e) => setForgotPasswordData({...forgotPasswordData, email: e.target.value})}
                      required
                      data-testid="forgot-email-input"
                    />
                    <Button type="submit" className="w-full" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={loading}>
                      {loading ? 'Sending...' : 'Send Reset Code'}
                    </Button>
                  </form>
                )}
                
                {forgotPasswordStep === 2 && (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <p className="text-sm text-center" style={{ color: '#64748B' }}>A 6-digit code has been sent to your email</p>
                    <Input
                      placeholder="Enter 6-digit code"
                      value={forgotPasswordData.reset_code}
                      onChange={(e) => setForgotPasswordData({...forgotPasswordData, reset_code: e.target.value})}
                      required
                      data-testid="reset-code-input"
                      maxLength={6}
                    />
                    <Input
                      type="password"
                      placeholder="New Password"
                      value={forgotPasswordData.new_password}
                      onChange={(e) => setForgotPasswordData({...forgotPasswordData, new_password: e.target.value})}
                      required
                      data-testid="new-password-input"
                    />
                    <Input
                      type="password"
                      placeholder="Confirm Password"
                      value={forgotPasswordData.confirm_password}
                      onChange={(e) => setForgotPasswordData({...forgotPasswordData, confirm_password: e.target.value})}
                      required
                      data-testid="confirm-password-input"
                    />
                    <Button type="submit" className="w-full" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={loading}>
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </form>
                )}
                
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordStep(1);
                    setForgotPasswordData({ email: '', reset_code: '', new_password: '', confirm_password: '' });
                  }}
                  className="text-sm w-full text-center font-medium"
                  style={{ color: '#0F172A' }}
                >
                  Back to login
                </button>
              </div>
            ) : (
              <>
            <Tabs value={authMode} onValueChange={setAuthMode} className="mb-6">
              <TabsList className="grid w-full grid-cols-2" style={{ backgroundColor: '#F1F5F9' }}>
                <TabsTrigger 
                  value="email" 
                  data-testid="email-tab"
                  style={authMode === 'email' ? { backgroundColor: '#D4AF37', color: '#0F172A' } : {}}
                >
                  Email
                </TabsTrigger>
                <TabsTrigger 
                  value="mobile" 
                  data-testid="mobile-tab"
                  style={authMode === 'mobile' ? { backgroundColor: '#D4AF37', color: '#0F172A' } : {}}
                >
                  Mobile
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="email">
                {signupOtpStep ? (
                  // OTP Verification Step for Signup
                  <form onSubmit={handleVerifySignupOtp} className="space-y-4">
                    <div className="text-center mb-4">
                      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                        <MessageSquare className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-lg">Verify Your Phone</h3>
                      <p className="text-sm text-muted-foreground">
                        OTP sent to {emailData.mobile.slice(0, 3)}****{emailData.mobile.slice(-4)}
                      </p>
                    </div>
                    <Input
                      placeholder="Enter 6-digit OTP"
                      value={signupOtp}
                      onChange={(e) => setSignupOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                      className="text-center text-2xl tracking-widest"
                      data-testid="signup-otp-input"
                    />
                    <Button 
                      type="submit" 
                      className="w-full" 
                      style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} 
                      disabled={loading || signupOtp.length !== 6}
                      data-testid="verify-signup-otp-btn"
                    >
                      {loading ? 'Verifying...' : 'Verify & Create Account'}
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setSignupOtpStep(false);
                        setSignupOtp('');
                        setSignupToken('');
                      }}
                      className="text-sm w-full text-center font-medium"
                      style={{ color: '#0F172A' }}
                    >
                      ← Back to signup form
                    </button>
                  </form>
                ) : (
                <form onSubmit={handleEmailAuth} className="space-y-4">
                  {isSignup && (
                    <>
                      <Input
                        placeholder="Full Name"
                        value={emailData.name}
                        onChange={(e) => setEmailData({...emailData, name: e.target.value})}
                        required
                        data-testid="name-input"
                      />
                      <Input
                        placeholder="Mobile Number (for OTP verification)"
                        value={emailData.mobile}
                        onChange={(e) => setEmailData({...emailData, mobile: e.target.value})}
                        required
                        data-testid="mobile-input"
                      />
                    </>
                  )}
                  <Input
                    type="email"
                    placeholder="Email"
                    value={emailData.email}
                    onChange={(e) => setEmailData({...emailData, email: e.target.value})}
                    required
                    data-testid="email-input"
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={emailData.password}
                    onChange={(e) => setEmailData({...emailData, password: e.target.value})}
                    required
                    data-testid="password-input"
                  />
                  <Button type="submit" className="w-full" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={loading} data-testid="email-submit-btn">
                    {loading ? 'Please wait...' : (isSignup ? 'Continue with OTP' : 'Log In')}
                  </Button>
                  {isSignup && (
                    <p className="text-xs text-center text-muted-foreground">
                      You'll receive an OTP on WhatsApp to verify your phone number
                    </p>
                  )}
                  {!isSignup && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm w-full text-center font-medium"
                      style={{ color: '#D4AF37' }}
                      data-testid="forgot-password-btn"
                    >
                      Forgot Password?
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsSignup(!isSignup)}
                    className="text-sm w-full text-center font-medium"
                    style={{ color: '#0F172A' }}
                    data-testid="toggle-signup-btn"
                  >
                    {isSignup ? 'Already have an account? Log in' : 'New user? Sign up'}
                  </button>
                </form>
                )}
              </TabsContent>
              
              <TabsContent value="mobile">
                {!otpSent ? (
                  <form onSubmit={handleSendOTP} className="space-y-4">
                    <Input
                      placeholder="Mobile Number"
                      value={mobileData.mobile}
                      onChange={(e) => setMobileData({...mobileData, mobile: e.target.value})}
                      required
                      data-testid="otp-mobile-input"
                    />
                    <Button type="submit" className="w-full" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={loading} data-testid="send-otp-btn">
                      {loading ? 'Sending...' : 'Send OTP'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    <Input
                      placeholder="Enter OTP"
                      value={mobileData.otp}
                      onChange={(e) => setMobileData({...mobileData, otp: e.target.value})}
                      required
                      data-testid="otp-input"
                    />
                    <Button type="submit" className="w-full" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={loading} data-testid="verify-otp-btn">
                      {loading ? 'Verifying...' : 'Verify OTP'}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setOtpSent(false)}
                      className="text-sm w-full text-center font-medium"
                      style={{ color: '#0F172A' }}
                    >
                      Change mobile number
                    </button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 font-semibold" style={{ color: '#64748B' }}>Or</span>
              </div>
            </div>
            
            <Button
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full"
              style={{ borderColor: '#D4AF37', color: '#0F172A' }}
              data-testid="google-login-btn"
            >
              Continue with Google
            </Button>
            
            <button
              onClick={() => setShowAuth(false)}
              className="mt-4 text-sm w-full text-center font-medium"
              style={{ color: '#0F172A' }}
            >
              Back to home
            </button>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}>
        <div className="absolute inset-0 opacity-10 noise-texture"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-6">
              <Crown className="w-12 h-12" style={{ color: '#D4AF37' }} />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4" style={{ color: '#FFFFFF', fontWeight: '700', opacity: 1 }}>
              Lions Club
            </h1>
            <p className="text-xl md:text-2xl mb-2 font-bold" style={{ color: '#D4AF37', opacity: 1 }}>
              Jaipur Hawamahal
            </p>
            <p className="text-lg font-medium max-w-2xl mx-auto" style={{ color: '#F1F5F9', opacity: 1 }}>
              Empowering communities through service, fellowship, and leadership
            </p>
          </div>
          
          <div className="flex flex-wrap gap-4 justify-center mb-16">
            <Button
              onClick={() => setShowAuth(true)}
              size="lg"
              className="rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              style={{ backgroundColor: '#D4AF37', color: '#0F172A', opacity: 1 }}
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
            <a href={KANYA_VIVAH_URL} target="_blank" rel="noopener noreferrer" data-testid="kanya-vivah-hero-link">
              <Button
                size="lg"
                className="rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                style={{ backgroundColor: '#8B1E2B', color: '#FFFFFF', opacity: 1 }}
              >
                <Heart className="w-5 h-5 mr-2" fill="currentColor" /> 101 Kanya Vivah
              </Button>
            </a>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* 101 Kanya Vivah Card (external app) */}
          <a href={KANYA_VIVAH_URL} target="_blank" rel="noopener noreferrer" className="block md:col-span-2 lg:col-span-3" data-testid="feature-kanya-vivah">
            <Card className="hover-lift cursor-pointer transition-all duration-300 hover:shadow-xl overflow-hidden border-0" style={{ background: 'linear-gradient(120deg, #0F172A 0%, #8B1E2B 100%)' }}>
              <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                <div className="shrink-0 grid place-items-center w-16 h-16 rounded-full" style={{ backgroundColor: 'rgba(212,175,55,0.15)' }}>
                  <Heart className="w-8 h-8" style={{ color: '#D4AF37' }} fill="currentColor" />
                </div>
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#D4AF37' }}>New Initiative · 10 March 2027</div>
                  <h3 className="text-2xl font-bold mb-1" style={{ color: '#FFFFFF' }}>101 Kanya Vivah · 101 कन्या विवाह</h3>
                  <p className="text-sm font-medium" style={{ color: '#F1F5F9' }}>A collective wedding for 101 couples in Jaipur. Apply for registration, track your application, and more.</p>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold shrink-0" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}>Open <ExternalLink className="w-4 h-4" /></span>
              </CardContent>
            </Card>
          </a>
          {/* Events & Activities Card */}
          <Card 
            className="hover-lift border-border/40 cursor-pointer transition-all duration-300 hover:shadow-xl hover:border-amber-300" 
            data-testid="feature-events"
            onClick={() => setShowEventsModal(true)}
          >
            <CardContent className="p-6 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4" style={{ color: '#D4AF37', opacity: 1 }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0F172A' }}>Events & Activities</h3>
              <p className="text-sm font-medium mb-3" style={{ color: '#334155', opacity: 1 }}>
                Stay updated with upcoming events and community service projects
              </p>
              {events.length > 0 && (
                <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#D4AF37' }}>
                  {events.length} Events <ChevronRight className="w-4 h-4" />
                </span>
              )}
            </CardContent>
          </Card>
          
          {/* In News Card */}
          <Card 
            className="hover-lift border-border/40 cursor-pointer transition-all duration-300 hover:shadow-xl hover:border-amber-300" 
            data-testid="feature-news"
            onClick={() => setShowNewsModal(true)}
          >
            <CardContent className="p-6 text-center">
              <Newspaper className="w-12 h-12 mx-auto mb-4" style={{ color: '#D4AF37', opacity: 1 }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0F172A' }}>In News</h3>
              <p className="text-sm font-medium mb-3" style={{ color: '#334155', opacity: 1 }}>
                Latest news and updates from Lions Club Jaipur Hawamahal
              </p>
              {news.length > 0 && (
                <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#D4AF37' }}>
                  {news.length} News <ChevronRight className="w-4 h-4" />
                </span>
              )}
            </CardContent>
          </Card>
          
          {/* Education Blogs Card */}
          <Card 
            className="hover-lift border-border/40 cursor-pointer transition-all duration-300 hover:shadow-xl hover:border-amber-300" 
            data-testid="feature-blogs"
            onClick={() => setShowBlogsModal(true)}
          >
            <CardContent className="p-6 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: '#D4AF37', opacity: 1 }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0F172A' }}>Education Blogs</h3>
              <p className="text-sm font-medium mb-3" style={{ color: '#334155', opacity: 1 }}>
                Educational content and resources for our community
              </p>
              {blogs.length > 0 && (
                <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#D4AF37' }}>
                  {blogs.length} Blogs <ChevronRight className="w-4 h-4" />
                </span>
              )}
            </CardContent>
          </Card>
          
          {/* Member Directory Card */}
          <Card 
            className="hover-lift border-border/40 cursor-pointer transition-all duration-300 hover:shadow-xl hover:border-amber-300" 
            data-testid="feature-members"
            onClick={() => setShowAuth(true)}
          >
            <CardContent className="p-6 text-center">
              <Users className="w-12 h-12 mx-auto mb-4" style={{ color: '#D4AF37', opacity: 1 }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0F172A' }}>Member Directory</h3>
              <p className="text-sm font-medium mb-3" style={{ color: '#334155', opacity: 1 }}>
                Connect with fellow Lions members and build lasting relationships
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#D4AF37' }}>
                Login to View <ChevronRight className="w-4 h-4" />
              </span>
            </CardContent>
          </Card>
          
          {/* Photo Gallery Card */}
          <Card 
            className="hover-lift border-border/40 cursor-pointer transition-all duration-300 hover:shadow-xl hover:border-amber-300" 
            data-testid="feature-gallery"
            onClick={() => setShowAuth(true)}
          >
            <CardContent className="p-6 text-center">
              <ImageIcon className="w-12 h-12 mx-auto mb-4" style={{ color: '#D4AF37', opacity: 1 }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0F172A' }}>Photo Gallery</h3>
              <p className="text-sm font-medium mb-3" style={{ color: '#334155', opacity: 1 }}>
                Explore memories from our events and community initiatives
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#D4AF37' }}>
                Login to View <ChevronRight className="w-4 h-4" />
              </span>
            </CardContent>
          </Card>
          
          {/* Admin Portal Card */}
          <Card 
            className="hover-lift border-border/40 cursor-pointer transition-all duration-300 hover:shadow-xl hover:border-amber-300" 
            data-testid="feature-admin"
            onClick={() => setShowAuth(true)}
          >
            <CardContent className="p-6 text-center">
              <Crown className="w-12 h-12 mx-auto mb-4" style={{ color: '#D4AF37', opacity: 1 }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#0F172A' }}>Admin Portal</h3>
              <p className="text-sm font-medium mb-3" style={{ color: '#334155', opacity: 1 }}>
                Powerful tools for club administrators to manage operations
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#D4AF37' }}>
                Login to Access <ChevronRight className="w-4 h-4" />
              </span>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Events List Modal */}
      <Dialog open={showEventsModal} onOpenChange={setShowEventsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Calendar className="w-6 h-6" style={{ color: '#D4AF37' }} />
              Events & Activities
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 mt-4">
            {events.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No events available at the moment.</p>
            ) : (
              events.map((event) => (
                <Card 
                  key={event.event_id} 
                  className="cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => { setShowEventsModal(false); setSelectedEvent(event); }}
                >
                  <CardContent className="p-4 flex gap-4">
                    {event.banner_image_url ? (
                      <img 
                        src={getImageUrl(event.banner_image_url)} 
                        alt={event.title} 
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => { 
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23d4af37"><rect width="24" height="24" rx="4" fill="%23f3f4f6"/><path d="M4 15l4-4 3 3 5-5 4 4V6a2 2 0 00-2-2H6a2 2 0 00-2 2v9z"/></svg>';
                        }}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-lg flex-shrink-0 bg-gray-100 flex items-center justify-center">
                        <Calendar className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1" style={{ color: '#0F172A' }}>{event.title}</h3>
                      <p className="text-sm text-muted-foreground mb-1">
                        <strong>Event:</strong> {(event.event_date || event.date_time_start) ? formatDate(event.event_date || event.date_time_start) : 'Date TBA'}
                        {event.event_time && ` at ${event.event_time}`}
                      </p>
                      {event.venue && <p className="text-sm text-muted-foreground mb-1">📍 {event.venue}</p>}
                      {event.registration_form_id && (event.registration_start_date || event.registration_end_date) && (
                        <p className="text-sm text-blue-600 mb-1">
                          <strong>Registration:</strong> {event.registration_start_date ? formatDate(event.registration_start_date) : 'Open'} 
                          {event.registration_end_date && ` - ${formatDate(event.registration_end_date)}`}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {event.registration_form_id && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            Registration Open
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-sm font-medium" style={{ color: '#D4AF37' }}>
                          View Details <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* News List Modal */}
      <Dialog open={showNewsModal} onOpenChange={setShowNewsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Newspaper className="w-6 h-6" style={{ color: '#D4AF37' }} />
              In News
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 mt-4">
            {news.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No news available at the moment.</p>
            ) : (
              news.map((item) => (
                <Card 
                  key={item.news_id} 
                  className="cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => { setShowNewsModal(false); setSelectedNews(item); }}
                >
                  <CardContent className="p-4 flex gap-4">
                    {item.media_url ? (
                      <img 
                        src={getImageUrl(item.media_url)} 
                        alt={item.title} 
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-lg flex-shrink-0 bg-gray-100 flex items-center justify-center">
                        <Newspaper className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                          {item.type}
                        </span>
                        <span className="text-xs text-muted-foreground">{formatDate(item.created_at)}</span>
                      </div>
                      <h3 className="font-semibold text-lg mb-1" style={{ color: '#0F172A' }}>{item.title}</h3>
                      {item.description && <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>}
                      <div className="flex items-center gap-1 text-sm mt-2 font-medium" style={{ color: '#D4AF37' }}>
                        View Details <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Blogs List Modal */}
      <Dialog open={showBlogsModal} onOpenChange={setShowBlogsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <BookOpen className="w-6 h-6" style={{ color: '#D4AF37' }} />
              Education Blogs
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 mt-4">
            {blogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No blogs available at the moment.</p>
            ) : (
              blogs.map((item) => (
                <Card 
                  key={item.blog_id} 
                  className="cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => { 
                    setShowBlogsModal(false);
                    if (item.external_link) {
                      setExternalLinkUrl(item.external_link);
                    } else {
                      setSelectedBlog(item); 
                    }
                  }}
                >
                  <CardContent className="p-4 flex gap-4">
                    {(item.thumbnail_url || item.media_url) ? (
                      <img 
                        src={getImageUrl(item.thumbnail_url || item.media_url)} 
                        alt={item.title} 
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-lg flex-shrink-0 bg-gray-100 flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                          {item.type}
                        </span>
                        {item.tags?.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">{tag}</span>
                        ))}
                      </div>
                      <h3 className="font-semibold text-lg mb-1" style={{ color: '#0F172A' }}>{item.title}</h3>
                      {item.description && <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>}
                      <div className="flex items-center gap-1 text-sm mt-2 font-medium" style={{ color: '#D4AF37' }}>
                        {item.external_link ? 'View Content' : 'Read More'} <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedEvent && (
            <div>
              {selectedEvent.banner_image_url && (
                <div className="mb-4 -mx-6 -mt-6 bg-gray-100">
                  <img 
                    src={getImageUrl(selectedEvent.banner_image_url)} 
                    alt={selectedEvent.title} 
                    className="w-full max-h-80 object-cover"
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  />
                </div>
              )}
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#0F172A' }}>{selectedEvent.title}</h2>
              
              {/* Event badges */}
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedEvent.visibility && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${selectedEvent.visibility === 'public' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                    {selectedEvent.visibility === 'public' ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                    {selectedEvent.visibility === 'public' ? 'Public Event' : 'Members Only'}
                  </span>
                )}
                {selectedEvent.payment_type && selectedEvent.payment_type !== 'free' && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    {selectedEvent.registration_amount ? `₹${selectedEvent.registration_amount}` : 'Paid'}
                  </span>
                )}
                {selectedEvent.registration_form_id && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 inline-flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Registration Open
                  </span>
                )}
              </div>

              {/* Event Details */}
              <div className="space-y-3 mb-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium">Event Date & Time</p>
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    {selectedEvent.event_date ? formatDate(selectedEvent.event_date) : (selectedEvent.date_time_start ? formatDate(selectedEvent.date_time_start) : 'Date to be announced')}
                    {selectedEvent.event_time && ` at ${selectedEvent.event_time}`}
                    {selectedEvent.date_time_start && !selectedEvent.event_time && (
                      <span className="text-muted-foreground">
                        at {new Date(selectedEvent.date_time_start).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </p>
                </div>
                {selectedEvent.venue && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium">Venue</p>
                    <p className="text-sm font-medium">📍 {selectedEvent.venue}</p>
                  </div>
                )}
                {selectedEvent.registration_form_id && (selectedEvent.registration_start_date || selectedEvent.registration_end_date) && (
                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs text-muted-foreground uppercase font-medium">Registration Period</p>
                    <p className="text-sm font-medium text-blue-600">
                      {selectedEvent.registration_start_date 
                        ? formatDate(selectedEvent.registration_start_date)
                        : 'Now'
                      }
                      {' - '}
                      {selectedEvent.registration_end_date 
                        ? formatDate(selectedEvent.registration_end_date)
                        : 'Until Event'
                      }
                    </p>
                  </div>
                )}
              </div>
              {selectedEvent.description && (
                <div className="prose max-w-none whitespace-pre-wrap">{selectedEvent.description}</div>
              )}
              {selectedEvent.event_images && selectedEvent.event_images.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold mb-2">Event Gallery</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedEvent.event_images.map((img, idx) => (
                      <img 
                        key={idx} 
                        src={getImageUrl(img)} 
                        alt={`Event ${idx + 1}`} 
                        className="w-full h-24 object-cover rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Registration Button */}
              {selectedEvent.registration_form_id && (
                <div className="mt-6 pt-4 border-t">
                  <Button
                    onClick={() => handleOpenRegistration(selectedEvent)}
                    className="w-full"
                    style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                    data-testid="register-event-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Register for this Event
                    {selectedEvent.registration_amount && ` - ₹${selectedEvent.registration_amount}`}
                  </Button>
                  {selectedEvent.visibility === 'members_only' && (
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      Login required for members-only events
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* News Detail Modal */}
      <Dialog open={!!selectedNews} onOpenChange={() => setSelectedNews(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedNews && (
            <div>
              {selectedNews.media_url && (
                <div className="mb-4 -mx-6 -mt-6 bg-gray-100">
                  {selectedNews.type === 'video' ? (
                    <video 
                      src={getImageUrl(selectedNews.media_url)} 
                      className="w-full max-h-80 object-contain bg-black" 
                      controls 
                      autoPlay
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <img 
                      src={getImageUrl(selectedNews.media_url)} 
                      alt={selectedNews.title} 
                      className="w-full max-h-80 object-cover"
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                  )}
                </div>
              )}
              <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                {selectedNews.type}
              </span>
              <h2 className="text-2xl font-bold mt-2 mb-2" style={{ color: '#0F172A' }}>{selectedNews.title}</h2>
              <p className="text-sm text-muted-foreground mb-4">{formatDate(selectedNews.created_at)}</p>
              {selectedNews.description && <p className="text-muted-foreground mb-4">{selectedNews.description}</p>}
              {selectedNews.article_content && <div className="prose max-w-none whitespace-pre-wrap">{selectedNews.article_content}</div>}
              {selectedNews.external_link && (
                <Button
                  onClick={() => { setSelectedNews(null); setExternalLinkUrl(selectedNews.external_link); }}
                  className="mt-4 font-medium" 
                  style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                >
                  View Content <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Blog Detail Modal */}
      <Dialog open={!!selectedBlog} onOpenChange={() => setSelectedBlog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBlog && (
            <div>
              {(selectedBlog.thumbnail_url || selectedBlog.media_url) && (
                <div className="mb-4 -mx-6 -mt-6 bg-gray-100">
                  {selectedBlog.type === 'video' && selectedBlog.media_url ? (
                    <video 
                      src={getImageUrl(selectedBlog.media_url)} 
                      className="w-full max-h-80 object-contain bg-black" 
                      controls 
                      autoPlay
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <img 
                      src={getImageUrl(selectedBlog.thumbnail_url || selectedBlog.media_url)} 
                      alt={selectedBlog.title} 
                      className="w-full max-h-80 object-cover"
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                  {selectedBlog.type}
                </span>
                {selectedBlog.tags?.map(tag => (
                  <span key={tag} className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">{tag}</span>
                ))}
              </div>
              <h2 className="text-2xl font-bold mt-2 mb-2" style={{ color: '#0F172A' }}>{selectedBlog.title}</h2>
              <p className="text-sm text-muted-foreground mb-4">{formatDate(selectedBlog.created_at)}</p>
              {selectedBlog.description && <p className="text-muted-foreground mb-4">{selectedBlog.description}</p>}
              {selectedBlog.content && <div className="prose max-w-none whitespace-pre-wrap">{selectedBlog.content}</div>}
              {selectedBlog.external_link && (
                <Button
                  onClick={() => { setSelectedBlog(null); setExternalLinkUrl(selectedBlog.external_link); }}
                  className="mt-4 font-medium" 
                  style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                >
                  View Content <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* External Link Iframe Modal */}
      <Dialog open={!!externalLinkUrl} onOpenChange={() => setExternalLinkUrl(null)}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5" style={{ color: '#D4AF37' }} />
              External Content
            </DialogTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => window.open(externalLinkUrl, '_blank')}
              className="text-xs"
            >
              Open in New Tab <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </DialogHeader>
          {externalLinkUrl && (
            <iframe
              src={externalLinkUrl}
              title="External Content"
              className="w-full h-full border-0"
              style={{ height: 'calc(90vh - 60px)' }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Event Registration Modal */}
      <Dialog open={showRegistrationModal} onOpenChange={() => { setShowRegistrationModal(false); setRegistrationEvent(null); setRegistrationForm(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Register for Event
            </DialogTitle>
            {registrationEvent && (
              <p className="text-sm text-muted-foreground">{registrationEvent.title}</p>
            )}
          </DialogHeader>
          
          {registrationEvent && registrationForm && (
            <EventRegistrationForm
              event={registrationEvent}
              form={registrationForm}
              bankDetails={bankDetails}
              isAuthenticated={false}
              onSuccess={() => {
                setShowRegistrationModal(false);
                setRegistrationEvent(null);
                setRegistrationForm(null);
                toast.success('Registration submitted! You will receive a confirmation email.');
              }}
              onClose={() => {
                setShowRegistrationModal(false);
                setRegistrationEvent(null);
                setRegistrationForm(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <footer className="py-6 border-t" style={{ backgroundColor: '#0F172A', borderColor: '#1E293B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-medium" style={{ color: '#94A3B8' }}>
            © {new Date().getFullYear()} All rights reserved with{' '}
            <span style={{ color: '#D4AF37' }}>Cupid Compusoft Pvt. Ltd.</span>
          </p>
          <p className="text-xs mt-2" style={{ color: '#64748B' }}>
            Designed, Developed and Conceptualized by CCPL
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
